import React from 'react';

const formatContextTimestamp = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};

/** Presentation-only context view for the regional forecasting workspace. */
const RegionalContextStage = ({ vm }) => {
  const { CartesianGrid, ComposedChart, Database, DollarSign, FORECAST_YEARS, Legend, Line, MACRO_SETS, NumericInput, RefreshCw, ResponsiveContainer, ShieldCheck, SlidersHorizontal, Tooltip, WaterfallChart, XAxis, YAxis, activeMacro, activeWorkspaceStage, bridgeDomain, bridgeRows, centralPlanRows, centralSaveError, centralSaveState, centralSavedAt, centralSettings, chartAxisColor, chartGridColor, formatMillions, formatPercent, isCentral, macroAnnualPath, macroDivergenceRows, macroMode, model, rateFeed, refreshRates, retryCentralSettingSave, setMacroMode, tooltipStyle, updateCentralSetting, macroStaleness, macroChangeDiffRows, macroReanchorMode, macroStaleDialogOpen, setMacroStaleDialogOpen, runWorkspaceReanchor, reanchorBusy, reanchorError, reanchorResult } = vm;
  const macroStale = Boolean(macroStaleness && macroStaleness.stale);
  const formatDeviationPct = (value) =>
    (value == null || !Number.isFinite(Number(value)))
      ? '—'
      : `${Number(value) >= 0 ? '+' : ''}${Number(value).toFixed(1)}%`;
  const formatMacroCell = (value, digits = 2) =>
    (Number.isFinite(Number(value)) ? Number(value).toFixed(digits) : '—');
  const centralSavedAtLabel = formatContextTimestamp(centralSavedAt);
  const renderCentralSaveIndicator = () => {
    if (centralSaveState === 'saving') {
      return (
        <p className="forecast-submit-note" role="status" aria-live="polite" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <RefreshCw size={14} aria-hidden="true" />
          Saving central settings...
        </p>
      );
    }
    if (centralSaveState === 'saved') {
      return (
        <p className="forecast-submit-note" role="status" aria-live="polite" style={{ marginTop: 8 }}>
          Saved at {centralSavedAtLabel || 'just now'}.
        </p>
      );
    }
    if (centralSaveState === 'error') {
      return (
        <div className="forecast-submit-note" role="alert" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
          <span className="forecast-health-chip red">Save failed</span>
          <span>{centralSaveError || 'Central settings save failed.'}</span>
          <button type="button" className="forecast-secondary-action" onClick={retryCentralSettingSave}>
            Retry
          </button>
        </div>
      );
    }
    return null;
  };

  return (
    <>
{activeWorkspaceStage === 'context' && <section className="forecast-panel workspace-stage-panel" id="regional-stage-context" tabIndex="-1">
        <div className="workspace-action-row" style={{ marginBottom: 10 }}>
          <span className="forecast-section-code">Planning case</span>
          <div className="forecast-segmented" role="group" aria-label="Planning case">
            {Object.keys(MACRO_SETS).map((mode) => (
              <button type="button" aria-pressed={macroMode === mode} className={macroMode === mode ? 'active' : ''} onClick={() => setMacroMode(mode)} key={mode}>{mode}</button>
            ))}
          </div>
          {macroStale && macroReanchorMode !== 'unavailable' && (
            <div className="macro-stale-chip-group" data-testid="macro-stale-chip" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="forecast-health-chip amber"
                title="A newer macro scenario version has been published. Review the changes."
                onClick={() => setMacroStaleDialogOpen(true)}
                style={{ cursor: 'pointer' }}
              >
                {macroStaleness.label}
              </button>
              <button type="button" className="forecast-secondary-action" onClick={() => setMacroStaleDialogOpen(true)}>
                Review changes
              </button>
              {macroReanchorMode === 'enabled' && (
                <button
                  type="button"
                  className="forecast-secondary-action"
                  disabled={reanchorBusy}
                  onClick={runWorkspaceReanchor}
                >
                  <RefreshCw size={13} /> {reanchorBusy ? 'Re-anchoring…' : `Re-anchor to v${macroStaleness.publishedVersion}`}
                </button>
              )}
              {macroReanchorMode === 'blocked' && (
                <span className="forecast-submit-note" data-testid="macro-reanchor-blocked">
                  Reviewer must return/reopen before re-anchoring.
                </span>
              )}
            </div>
          )}
        </div>
        {macroStale && reanchorError && (
          <p className="forecast-submit-note" role="alert" data-testid="macro-reanchor-error">{reanchorError}</p>
        )}
        {macroStale && reanchorResult && (
          <div className="forecast-subpanel" data-testid="macro-reanchor-result" style={{ marginBottom: 10 }}>
            <p className="forecast-submit-note">
              Re-anchored to {reanchorResult.scenarioKey} v{reanchorResult.scenarioVersion} — all local levers preserved.
              Driver-view shift (deviation vs anchor, before → after):
            </p>
            {(reanchorResult.beforeAfter || []).length > 0 && (
              <div className="forecast-table-shell ace-scroll-shell forecast-compact-table">
                <table className="forecast-table">
                  <thead>
                    <tr><th>Metric</th><th>FY</th><th>Before</th><th>After</th></tr>
                  </thead>
                  <tbody>
                    {reanchorResult.beforeAfter.map((row) => (
                      <tr key={row.key} className={row.exceeded ? 'variance-neg' : ''}>
                        <td><strong>{row.metricLabel}</strong></td>
                        <td>FY{row.year}</td>
                        <td>{formatDeviationPct(row.beforePct)}</td>
                        <td>{formatDeviationPct(row.afterPct)}{row.exceeded ? ' ●' : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        {macroStaleDialogOpen && (
          <div className="macro-note-dialog-backdrop" role="presentation" onClick={() => setMacroStaleDialogOpen(false)}>
            <div className="macro-note-dialog" role="dialog" aria-modal="true" aria-labelledby="macro-stale-dialog-title" onClick={(event) => event.stopPropagation()}>
              <div className="forecast-panel-head">
                <div>
                  <span className="forecast-section-code">Macro scenario changes</span>
                  <h2 id="macro-stale-dialog-title">
                    {macroStaleness.scenarioKey} v{macroStaleness.savedVersion} → v{macroStaleness.publishedVersion}
                  </h2>
                </div>
              </div>
              <p className="forecast-submit-note">
                Your plan is anchored to {macroStaleness.scenarioKey} v{macroStaleness.savedVersion}. The latest
                published version is v{macroStaleness.publishedVersion}. Changed macro variables (old → new):
              </p>
              <div className="forecast-table-shell ace-scroll-shell forecast-compact-table">
                <table className="forecast-table macro-diff-table">
                  <thead>
                    <tr><th>Variable</th><th>Year</th><th>Anchored</th><th>Published</th><th>Δ</th></tr>
                  </thead>
                  <tbody>
                    {(macroChangeDiffRows || []).length === 0 ? (
                      <tr><td colSpan="5">No variable-level changes detected between versions.</td></tr>
                    ) : macroChangeDiffRows.map((row) => (
                      <tr key={row.key}>
                        <td>{row.variable}</td>
                        <td>FY{row.year}</td>
                        <td className="forecast-mono">{formatMacroCell(row.from)}</td>
                        <td className="forecast-mono">{formatMacroCell(row.to)}</td>
                        <td className={`forecast-mono ${row.delta > 0 ? 'pos' : row.delta < 0 ? 'neg' : ''}`}>
                          {row.delta > 0 ? '+' : ''}{formatMacroCell(row.delta)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="forecast-submit-actions">
                {macroReanchorMode === 'enabled' && (
                  <button
                    type="button"
                    className="forecast-primary-button"
                    disabled={reanchorBusy}
                    onClick={() => { runWorkspaceReanchor(); setMacroStaleDialogOpen(false); }}
                  >
                    <RefreshCw size={14} /> Re-anchor to v{macroStaleness.publishedVersion}
                  </button>
                )}
                {macroReanchorMode === 'blocked' && (
                  <span className="forecast-submit-note">Reviewer must return/reopen before re-anchoring.</span>
                )}
                <button type="button" className="forecast-secondary-button" onClick={() => setMacroStaleDialogOpen(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        <details className="forecast-accordion" open>
          <summary>
            <div>
              <span className="forecast-section-code">OP_02 · Macro environment</span>
              <h2>Base economic scenario and annual divergence</h2>
            </div>
            <SlidersHorizontal size={18} />
          </summary>

          <div className="forecast-accordion-body">
            {isCentral && centralSettings && (
              <div className="forecast-subpanel central-editor">
                <div className="forecast-panel-head">
                  <div>
                    <span className="forecast-section-code">OP_02X · Central macro controls</span>
                    <h3>Corporate Planning only — changes propagate to every inputter and scenario</h3>
                  </div>
                  <ShieldCheck size={16} />
                </div>
                <div className="central-editor-grid">
                  {[
                    { key: 'sofr', label: 'SOFR %', step: 0.05, min: 0, max: 9 },
                    { key: 'gdp', label: 'GDP %', step: 0.1, min: -4, max: 6 },
                    { key: 'unemployment', label: 'Unemployment %', step: 0.1, min: 2, max: 12 },
                    { key: 'wageInflation', label: 'Wage inflation %', step: 0.1, min: 0, max: 8 },
                    { key: 'expenseInflation', label: 'Expense inflation %', step: 0.1, min: 0, max: 8 },
                    { key: 'creditLoss', label: 'Credit loss %', step: 0.02, min: 0, max: 3 },
                    { key: 'depositBeta', label: 'Deposit beta', step: 1, min: 5, max: 90 }
                  ].map((field) => (
                    <label key={field.key}>
                      <span>{field.label}</span>
                      <NumericInput
                        value={centralSettings.macroBase?.[field.key]}
                        step={field.step} min={field.min} max={field.max}
                        onCommit={(value) => updateCentralSetting('macroBase', field.key, value, field.label)}
                      />
                    </label>
                  ))}
                </div>
                <p className="forecast-submit-note">
                  Saved to the cycle's central settings ({centralSettings.updatedBy
                    ? `last touched by ${centralSettings.updatedBy}` : 'seed values'}
                  {centralSettings.updatedAt ? ` on ${formatContextTimestamp(centralSettings.updatedAt)}` : ''}). Upside and
                  downside shock sets shift with the base; every unit's baseline rebuilds on reload.
                </p>
                {renderCentralSaveIndicator()}
              </div>
            )}
            <div className="forecast-grid forecast-grid-2 forecast-grid-contained">
        <div className="forecast-panel forecast-panel-tall">
          <div className="forecast-panel-head">
            <div>
              <span className="forecast-section-code">OP_02 · Macro environment</span>
              <h2>Base economic scenario</h2>
            </div>
            <SlidersHorizontal size={18} />
          </div>

          <div className="forecast-rate-row">
            <div>
              <span>SOFR source</span>
              <strong>{rateFeed.status}</strong>
              <small>{rateFeed.lastUpdated}</small>
            </div>
            <button type="button" className="forecast-icon-button" onClick={refreshRates} title="Refresh SOFR from NY Fed">
              <RefreshCw size={15} />
              Refresh rates
            </button>
          </div>

          <div className="forecast-macro-metrics">
            <div>
              <span>SOFR</span>
              <strong>{formatPercent(activeMacro.sofr, 2)}</strong>
            </div>
            <div>
              <span>GLP / GDP</span>
              <strong>{formatPercent(activeMacro.gdp, 1)}</strong>
            </div>
            <div>
              <span>Deposit beta</span>
              <strong>{formatPercent(activeMacro.depositBeta, 0)}</strong>
            </div>
            <div>
              <span>Credit loss</span>
              <strong>{formatPercent(activeMacro.creditLoss, 2)}</strong>
            </div>
          </div>

          <div className="forecast-chart forecast-macro-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={macroAnnualPath} margin={{ top: 12, right: 14, left: 0, bottom: 8 }}>
                <CartesianGrid stroke={chartGridColor} vertical={false} strokeDasharray="3 5" />
                <XAxis dataKey="year" tick={{ fill: chartAxisColor, fontSize: 11 }} />
                <YAxis tick={{ fill: chartAxisColor, fontSize: 10 }} />
                <Tooltip contentStyle={tooltipStyle} formatter={(value) => formatPercent(value, 2)} />
                <Legend wrapperStyle={{ color: chartAxisColor, fontSize: 11 }} />
                <Line type="monotone" dataKey="baseSofr" name="Base SOFR" stroke="hsl(var(--chart-5))" strokeDasharray="4 4" dot={false} />
                <Line type="monotone" dataKey="scenarioSofr" name="Scenario SOFR" stroke="hsl(var(--chart-1))" strokeWidth={2.4} />
                <Line type="monotone" dataKey="baseCreditLoss" name="Base credit loss" stroke="hsl(var(--chart-4))" strokeDasharray="4 4" dot={false} />
                <Line type="monotone" dataKey="scenarioCreditLoss" name="Scenario credit loss" stroke="hsl(var(--destructive))" strokeWidth={2.2} />
                <Line type="monotone" dataKey="scenarioDepositRunoff" name="Scenario runoff" stroke="hsl(var(--chart-3))" strokeWidth={2.2} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="forecast-table-shell ace-scroll-shell forecast-compact-table">
            <table className="forecast-table forecast-macro-divergence-table">
              <thead>
                <tr>
                  <th>Macro metric</th>
                  {FORECAST_YEARS.map((year) => (
                    <th key={year}>FY{year} vs base</th>
                  ))}
                  <th>Avg. divergence</th>
                </tr>
              </thead>
              <tbody>
                {macroDivergenceRows.map((row) => (
                  <tr key={row.key}>
                    <td>
                      <strong>{row.label}</strong>
                    </td>
                    {row.annualValues.map((value) => (
                      <td key={`${row.key}-${value.year}`}>
                        {value.variance >= 0 ? '+' : ''}{formatPercent(value.variance, row.digits)}
                      </td>
                    ))}
                    <td>{row.totalVariance >= 0 ? '+' : ''}{formatPercent(row.totalVariance, row.digits)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="forecast-panel forecast-panel-tall">
          <div className="forecast-panel-head">
            <div>
              <span className="forecast-section-code">OP_02B · Scenario preview</span>
              <h2>Macro-adjusted forecast bridge</h2>
            </div>
            <DollarSign size={18} />
          </div>

          <div className="forecast-kpi-grid">
            <div>
              <span>Net hires</span>
              <strong>{model.netHires}</strong>
              <small>{model.grossHires} gross / {model.departures} departures</small>
            </div>
            <div>
              <span>Loan growth</span>
              <strong>{formatPercent(model.adjustedLoanGrowth)}</strong>
              <small>Limited by appetite cap</small>
            </div>
            <div>
              <span>Deposit growth</span>
              <strong>{formatPercent(model.adjustedDepositGrowth)}</strong>
              <small>After runoff stress</small>
            </div>
            <div>
              <span>NIM</span>
              <strong>{formatPercent(model.nim * 100, 2)}</strong>
              <small>NII / average loans</small>
            </div>
            <div>
              <span>Efficiency</span>
              <strong>{formatPercent(model.efficiencyRatio * 100, 1)}</strong>
              <small>NIE / revenue</small>
            </div>
            <div>
              <span>Net income</span>
              <strong>{formatMillions(model.netIncome)}</strong>
              <small>After provision and tax</small>
            </div>
          </div>

          <WaterfallChart
            rows={bridgeRows}
            domain={bridgeDomain}
            ariaLabel="Macro-adjusted forecast waterfall from actual run-rate to final forecast."
          />
          <p className="forecast-submit-note">
            Why a bridge on the baseline? Yes — the central macro assumptions actively reshape the
            baseline before any market input. Rates, credit, and inflation move the starting plan;
            your levers in OP_03 and OP_04 then move it again. Each bar names which force did what.
          </p>
        </div>
            </div>

        <div className="forecast-subpanel central-plan">
          <div className="forecast-panel-head">
            <div>
              <span className="forecast-section-code">OP_02C · Centrally planned lines</span>
              <h3>FP&A-owned, driver-based — allocated to this unit, not inputted here</h3>
            </div>
            <Database size={16} />
          </div>
          <div className="forecast-home-table-wrap">
            <table className="forecast-home-table op3-year-table">
              <thead>
                <tr>
                  <th>Line</th>
                  <th>Driver</th>
                  {FORECAST_YEARS.map((year) => <th key={year}>FY{year}</th>)}
                </tr>
              </thead>
              <tbody>
                {centralPlanRows.map((item) => (
                  <tr key={item.id}>
                    <td><strong>{item.label}</strong></td>
                    <td>
                      <small className="forecast-home-market">{item.driver}</small>
                      {isCentral && (
                        <span className="central-coef">
                          <NumericInput
                            value={item.coefValue} step={0.1} min={0} max={50}
                            title={item.coefLabel}
                            onCommit={(value) =>
                              updateCentralSetting('centralItems', item.coefKey, value, `${item.label} (${item.coefLabel})`)
                            }
                          />
                          <em>{item.coefLabel}</em>
                        </span>
                      )}
                    </td>
                    {item.byYear.map((value, index) => (
                      <td className="forecast-mono" key={FORECAST_YEARS[index]}>
                        {formatMillions(value)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="forecast-submit-note">
            These expense lines are dictated centrally and rebuilt from drivers each cycle (deposits
            for FDIC, lease and asset schedules, scenario credit losses) — they're shown so the
            business sees the full cost base it carries, but they are not market inputs. Questions
            route to FP&A, not into the levers below.
          </p>
        </div>
          </div>
        </details>
      </section>}
    </>
  );
};

export default RegionalContextStage;
